<template>
  <div class="admin-organization">
    <div class="adminOrganization__title">
      <d-workspace-group-breadcrumb hideArrow size="large" isShowOrganization />
      <div class="adminOrganization__tabs">
        <d-tabs
          v-model="currentTab"
          autoWidth
          :tabNames="tabNames"
          light
          height="48"
          :autotest="autotestTabs"
          :class="{ 'adminOrganization__tabs-focus-settings': showPart === 'settings' }"
          @input="clickTab"
        >
          <template slot="tabName" slot-scope="{ item, index }">
            <!-- TODO: Refactor `component` for `AdminSettingsMenu` of tabs. -->
            <component
              v-if="index === tabNames.length - 1"
              :is="item"
              :settingPage="settingPage"
              @setSettingPage="setSettingPage"
              class="adminOrganization__tabs__settingsMenu"
            ></component>
            <span v-else>{{ item }}</span>
          </template>
        </d-tabs>
      </div>
    </div>

    <!-- All Users table -->
    <div v-if="showPart === 'allUsers'" class="tab-div">
      <div class="tab-title-row">
        <p class="tab-title-new text-no-wrap" autotest="users-count">
          <span v-if="searchUser.length > 0">{{ $t("analytics.searchUser") }}</span>
          <span v-else>
            <b v-if="currentStatus == 'active'">{{ totalActive }}</b>
            <b v-if="currentStatus == 'inactive'">{{ totalInactive }}</b>
            {{ $t("all.users") }}
          </span>
        </p>
        <v-spacer></v-spacer>
        <d-button
          round
          depressed
          class="text-capitalize white--text body-2 create-btn"
          color="dBlue"
          light
          @click="showForm = 'invite-user'"
          autotest="invite-user-btn"
          >{{ $t("admin.addUser") }}</d-button
        >
      </div>

      <div class="table-search-row">
        <div>
          <d-table-search
            v-model="searchUser"
            :placeholder="$t('analytics.searchUser')"
            @input-search="debounceHandleFindUser($event)"
            autotest="user-search-bar"
          >
            <div v-if="searchUser.length == 0" class="filter-div">
              <user-status-filter
                autotest="users-status-filter"
                :statusDictionary="statusDictionary"
                :filterStatus="filterStatus"
                :currentStatus="currentStatus"
                @changeCurrentStatus="changeCurrentStatus"
              ></user-status-filter>
            </div>
          </d-table-search>
        </div>
      </div>

      <!-- active users table -->
      <div v-show="currentStatus === 'active'" class="table-div" @click="viewedUserId = ''">
        <d-data-table
          padding-bottom
          infinite-scroll
          :loading="isLoading.usersData || isSearchingUsers"
          :headers="userTableHeaders"
          :items="activeUsersData"
          :noSearchResults="noSearchResults && searchUser.length > 0"
          :loadedAllData="activeUsersData.length === totalActive"
          :showUserCard="viewedUserId.length > 0"
          @load-more="getMoreUsers"
          class="d-data-table"
        >
          <template slot="no-data" v-if="!this.searchUser">
            <span>{{ $t("admin.msgNoMembersYet") }}</span>
          </template>
          <template slot="items" slot-scope="props">
            <td class="text-xs-left text-capitalize text-no-wrap">
              <div class="avatar-div">
                <d-avatar :size="32" :userId="props.item.id" />
                <div v-if="props.item.role === 'admin-organization'" class="org-admin-icon">
                  <icon-base width="14" height="14" color="white">
                    <d-icon-org-admin />
                  </icon-base>
                </div>
                <div v-if="props.item.shared" class="org-admin-icon shared-icon">
                  <icon-base width="14" height="14" color="white">
                    <d-icon-shared-user />
                  </icon-base>
                </div>
              </div>
              <d-tooltip-show-all
                :text="props.item.displayName"
                class="ml-3"
                autotest="username-display"
              ></d-tooltip-show-all>
            </td>
            <td class="text-xs-left" autotest="user-email-display">
              <span v-if="props.item.passwordless">-</span>
              <span v-else>{{ props.item.email }}</span>
            </td>
            <td class="text-xs-left" autotest="employee-id-display">
              <d-tooltip-show-all :text="props.item.passwordless ? props.item.employeeId : '-'"></d-tooltip-show-all>
            </td>
            <td class="text-xs-left" autotest="last-activity-display">{{ props.item.lastActivityString }}</td>
            <td class="text-xs-left text-capitalize text-no-wrap" autotest="user-status-display">
              {{ statusDictionary[props.item.status] }}
            </td>
            <td class="new-btns-td">
              <div class="new-btns-td-div center-horizontally">
                <div
                  v-if="hasCustomUserAttributes"
                  class="view-button-new btn-hover"
                  @mouseenter="viewedUserId = props.item.id"
                  @click.stop="clickViewUserCard(props.item.id)"
                >
                  <icon-base width="22" height="22" opacity="0.8">
                    <d-icon-preview />
                  </icon-base>
                  <div>
                    <d-admin-user-card
                      class="user-card"
                      v-if="viewedUserId === props.item.id && props.item.id !== ''"
                      :userData="props.item.customAttributes"
                      :customUserAttributes="customUserAttributes"
                    ></d-admin-user-card>
                  </div>
                </div>
                <d-tooltip v-if="!props.item.invitation" :text="$t('admin.tooltipEditUser')" class="tooltip-btn-new">
                  <button @click="setUserEditor(props.item)" class="tooltip-btn-btn btn-hover" autotest="edit-user-btn">
                    <icon-base width="22" height="22">
                      <d-icon-pen />
                    </icon-base>
                  </button>
                </d-tooltip>
                <d-tooltip
                  v-if="props.item.status == 'invited' || props.item.status == 'invitation-expired'"
                  :text="$t('admin.tooltipResendInvite')"
                  class="tooltip-btn-new"
                >
                  <button
                    class="tooltip-btn-btn btn-hover"
                    @click="setResendInvitation(props.item)"
                    autotest="resend-invite-btn"
                  >
                    <icon-base width="22" height="22">
                      <d-icon-resend />
                    </icon-base>
                  </button>
                </d-tooltip>
                <d-tooltip
                  v-if="props.item.status == 'invited' || props.item.status == 'invitation-expired'"
                  :text="$t('admin.tooltipCancelInvite')"
                  class="cancel-invite-btn-new"
                >
                  <button
                    class="tooltip-btn-btn btn-hover"
                    @click="setDeleteInvitation(props.item)"
                    autotest="cancel-invite-btn"
                  >
                    <icon-base width="18" height="18">
                      <d-icon-close2 />
                    </icon-base>
                  </button>
                </d-tooltip>
                <v-menu v-if="showMoreBtn(props.item)" transition="slide-y-transition" bottom left offset-y>
                  <button slot="activator" class="tooltip-btn-btn btn-hover" autotest="more-action-btn">
                    <icon-base width="20" height="20">
                      <d-icon-more />
                    </icon-base>
                  </button>
                  <v-list dense light>
                    <v-list-tile
                      v-if="canUpgradeToOrgAdmin(props.item)"
                      @click="showUpgradeOrgPopup(props.item)"
                      autotest="upgrade-org-admin-btn"
                    >
                      <v-list-tile-title class="body-1">{{ $t("admin.upgradeToOrgAdmin") }}</v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile
                      v-if="canDowngradeOrgAdmin(props.item)"
                      @click="showDowngradeOrgPopup(props.item)"
                      autotest="downgrade-org-admin-btn"
                    >
                      <v-list-tile-title class="body-1">{{ $t("admin.downgradeOrgAdmin") }}</v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </div>
            </td>
          </template>
        </d-data-table>
      </div>

      <!-- Inactive users table -->
      <div v-show="currentStatus === 'inactive'" class="table-div" @click="viewedUserId = ''">
        <d-data-table
          padding-bottom
          infinite-scroll
          :loading="false"
          :headers="userTableHeaders"
          :items="inactiveUsersData"
          :noSearchResults="false"
          :loadedAllData="false"
          :showUserCard="viewedUserId.length > 0"
          @load-more="getMoreInactiveUsers"
          class="d-data-table"
        >
          <template slot="no-data" v-if="!this.searchUser">
            <span>{{ $t("admin.msgNoMembersYet") }}</span>
          </template>
          <template slot="items" slot-scope="props">
            <td class="text-xs-left text-capitalize text-no-wrap">
              <div class="avatar-div">
                <d-avatar :size="32" :userId="props.item.id" />
                <div v-if="props.item.role === 'admin-organization'" class="org-admin-icon">
                  <icon-base width="14" height="14" color="white">
                    <d-icon-org-admin />
                  </icon-base>
                </div>
                <div v-if="props.item.shared" class="org-admin-icon shared-icon">
                  <icon-base width="14" height="14" color="white">
                    <d-icon-shared-user />
                  </icon-base>
                </div>
                <!-- DIconSharedUser -->
              </div>
              <d-tooltip-show-all
                :text="props.item.displayName"
                class="ml-3"
                autotest="username-display"
              ></d-tooltip-show-all>
            </td>
            <td class="text-xs-left" autotest="user-email-display">
              <span v-if="props.item.passwordless">-</span>
              <span v-else>{{ props.item.email }}</span>
            </td>
            <td class="text-xs-left" autotest="employee-id-display">
              <d-tooltip-show-all :text="props.item.passwordless ? props.item.employeeId : '-'"></d-tooltip-show-all>
            </td>
            <td class="text-xs-left" autotest="last-activity-display">{{ props.item.lastActivityString }}</td>
            <td class="text-xs-left text-capitalize text-no-wrap" autotest="user-status-display">
              {{ statusDictionary[props.item.status] }}
            </td>
            <td class="new-btns-td">
              <div class="new-btns-td-div center-horizontally">
                <div
                  v-if="hasCustomUserAttributes"
                  class="view-button-new btn-hover"
                  @mouseenter="viewedUserId = props.item.id"
                  @click.stop="clickViewUserCard(props.item.id)"
                >
                  <icon-base width="22" height="22" opacity="0.8">
                    <d-icon-preview />
                  </icon-base>
                  <div>
                    <d-admin-user-card
                      class="user-card"
                      v-if="viewedUserId === props.item.id && props.item.id !== ''"
                      :userData="props.item.customAttributes"
                      :customUserAttributes="customUserAttributes"
                    ></d-admin-user-card>
                  </div>
                </div>
                <d-tooltip v-if="!props.item.invitation" :text="$t('admin.tooltipEditUser')" class="tooltip-btn-new">
                  <button @click="setUserEditor(props.item)" class="tooltip-btn-btn btn-hover" autotest="edit-user-btn">
                    <icon-base width="22" height="22">
                      <d-icon-pen />
                    </icon-base>
                  </button>
                </d-tooltip>
                <d-tooltip
                  v-if="props.item.status == 'invited' || props.item.status == 'invitation-expired'"
                  :text="$t('admin.tooltipResendInvite')"
                  class="tooltip-btn-new"
                >
                  <button
                    class="tooltip-btn-btn btn-hover"
                    @click="setResendInvitation(props.item)"
                    autotest="resend-invite-btn"
                  >
                    <icon-base width="22" height="22">
                      <d-icon-resend />
                    </icon-base>
                  </button>
                </d-tooltip>
                <d-tooltip
                  v-if="props.item.status == 'invited' || props.item.status == 'invitation-expired'"
                  :text="$t('admin.tooltipCancelInvite')"
                  class="cancel-invite-btn-new"
                >
                  <button
                    class="tooltip-btn-btn btn-hover"
                    @click="setDeleteInvitation(props.item)"
                    autotest="cancel-invite-btn"
                  >
                    <icon-base width="18" height="18">
                      <d-icon-close2 />
                    </icon-base>
                  </button>
                </d-tooltip>
              </div>
            </td>
          </template>
        </d-data-table>
      </div>
    </div>

    <!-- Workspace groups table -->
    <div v-if="$access.teams && showPart === 'workspaceGroups'" class="admin-setting-div">
      <admin-workspace-groups></admin-workspace-groups>
    </div>

    <!-- workspaces table -->
    <div v-if="$access.teams && showPart === 'workspaces' && isGroupsDataReady" class="admin-setting-div">
      <admin-workspace-group-workspaces :workspaceList="workspaceList" isOnOrganizationLevel />
    </div>

    <!-- Teams table -->
    <div v-if="$access.teams && showPart === 'teams' && isGroupsDataReady" class="admin-setting-div">
      <admin-workspace-teams :workspaceDictionary="groupDictionary" isOnOrganizationLevel />
    </div>

    <!-- Business table -->
    <div v-if="showPart === 'business'" class="tab-div">
      <div class="tab-title-row">
        <p class="tab-title text-no-wrap" autotest="business-count">
          <b>{{ businessData.length }}</b>
          {{ $t("all.business") }}
        </p>
        <v-spacer></v-spacer>
        <d-button
          round
          depressed
          class="text-capitalize white--text body-2 create-btn"
          color="dBlue"
          light
          @click="showForm = 'create-business'"
          autotest="create-business-btn"
          >{{ $t("admin.createBusiness") }}</d-button
        >
      </div>
      <div class="table-wrap">
        <d-data-table
          add-search
          local-search
          initial-sort
          :headers="businessTableHeaders"
          :loading="isLoading.businessData || isLoading.groupsData"
          :items="isLoading.businessData || isLoading.groupsData ? [] : businessTableData"
          :placeholder="$t('admin.hintSearchBusiness')"
          searchAutotest="business-search-bar"
        >
          <v-progress-linear slot="progress" color="dBlue" height="2" indeterminate></v-progress-linear>
          <template slot="no-data">
            <span>
              <!-- This Organization does not have Business yet. -->
              {{ $t("admin.msgNoBusinessYet") }}
            </span>
          </template>
          <template slot="no-results">
            <span>
              <!-- No matching records found in Business. -->
              {{ $t("admin.msgNoRecordsBusiness") }}
            </span>
          </template>
          <template slot="items" slot-scope="props">
            <td class="text-xs-left text-capitalize" autotest="business-name-display">{{ props.item.name }}</td>
            <td class="text-xs-left text-capitalize" autotest="workspace-group-name-display">
              {{ props.item.workspaceGroupName }}
            </td>
            <td class="text-xs-left text-capitalize" autotest="workspace-name-display">
              {{ props.item.workspaceName }}
            </td>
            <td class="text-xs-right">
              <button
                class="table-editor-btn btn-hover"
                @click="setEditorFormData(props.item), (showForm = 'edit-business')"
                autotest="business-edit-btn"
              >
                <icon-base width="22" height="22">
                  <d-icon-pen />
                </icon-base>
              </button>
            </td>
          </template>
        </d-data-table>
      </div>
    </div>

    <template v-if="showPart === 'settings'">
      <admin-categories v-if="$access.categoriesCreation && settingPage === 'categories'"></admin-categories>

      <admin-languages v-if="$access.languages && settingPage === 'languages'"></admin-languages>

      <d-admin-permissions v-if="$access.permissions && settingPage === 'permissions'"></d-admin-permissions>

      <d-admin-ip-restriction
        v-if="getter_ip_restriction_feature_enabled && settingPage === 'ip-restriction'"
      ></d-admin-ip-restriction>

      <admin-master-password
        v-if="getter_has_employee_id_login && settingPage === 'master-password'"
      ></admin-master-password>

      <d-admin-sso-mapping v-if="$access.ssoMapping && settingPage === 'sso-mapping'"></d-admin-sso-mapping>

      <admin-company-logo v-if="$access.companyLogo && settingPage === 'company-logo'" />
    </template>

    <!-- components for all users-->
    <admin-add-user-popup
      v-if="showForm === 'invite-user'"
      :showForm="showForm"
      :groupDictionary="groupDictionary"
      @closeAdminInvite="showForm = ''"
      @invitedFailed="isInvitedFailed = true"
      @success-add-user="totalActive += 1"
    ></admin-add-user-popup>

    <admin-form-user-editor
      v-if="showForm === 'user-editor'"
      :parent="'admin-organization'"
      :formData="formData"
      :roleOptions="allRoleOptions"
      :userRole="userRole"
      :groupId="groupId"
      :groupDictionary="groupDictionary"
      @closeUserEditor="showForm = ''"
      @update-found-user="updateFoundUser($event, 'update')"
    ></admin-form-user-editor>

    <upgrade-to-org-admin-confirmation
      v-if="showForm === 'upgrade-to-org'"
      :formData="formData"
      @update-success="updateTolatestUserdataFromDb"
      @closePopup="(showForm = ''), (formData = {})"
    ></upgrade-to-org-admin-confirmation>

    <downgrade-org-admin-confirmation
      v-if="showForm === 'downgrade-org'"
      :formData="formData"
      @update-success="updateTolatestUserdataFromDb"
      @closePopup="(showForm = ''), (formData = {})"
    ></downgrade-org-admin-confirmation>

    <d-alert v-model="isInvitedFailed" type="alert" :message="$t('admin.alertFailedInviteUser')">
      <!-- Failed to invite user -->
    </d-alert>

    <!-- components for business-->
    <admin-form-business-create-edit
      v-if="showForm === 'create-business' || showForm == 'edit-business'"
      :formData="formData"
      :showForm="showForm"
      :businessData="businessData"
      @closeCreateBusiness="(showForm = ''), (formData = {})"
      @reloadBusinessData="reloadBusinessData()"
      @addBusinessSuccess="addBusinessSuccess = true"
      @editBusinessSuccess="editBusinessSuccess = true"
    ></admin-form-business-create-edit>
    <d-alert
      v-model="addBusinessSuccess"
      type="success"
      :message="$t('admin.alertSuccessfullyCreatedBusiness')"
    ></d-alert>

    <d-alert
      v-model="editBusinessSuccess"
      type="success"
      :message="$t('admin.alertSuccessfullyEditedBusiness')"
    ></d-alert>

    <!-- delete Invite Confirmation -->
    <d-confirmation-popup
      v-model="deleteInviteConfirmation"
      light
      :title="$t('admin.deleteInvitation')"
      :cancelBtnName="$t('all.cancel')"
      :actionBtnName="$t('admin.popupBtnDelete')"
      :content="$t('admin.popupMsgDeleteInvitation') + formData.email + '?'"
      :autotest="{
        close: 'delete-invitation-popup-close-btn',
        action: 'delete-invitation-popup-yes-btn',
        cancel: 'delete-invitation-popup-cancel-btn',
      }"
      @clickActionBtn="deleteInvite()"
    ></d-confirmation-popup>

    <!-- resend invitation confirmation dialog -->
    <d-confirmation-popup
      v-model="resetSendInvitation"
      light
      :title="$t('admin.resendInvitation')"
      :cancelBtnName="$t('all.cancel')"
      :actionBtnName="$t('admin.popupBtnSend')"
      :content="$t('admin.popupMsgResendInvitation') + formData.email + '?'"
      :autotest="{
        close: 'resend-invitation-popup-close-btn',
        action: 'resend-invitation-popup-yes-btn',
        cancel: 'resend-invitation-popup-cancel-btn',
      }"
      @clickActionBtn="resendInvitation()"
    ></d-confirmation-popup>
    <d-alert
      v-model="resetSendInvitationAlert.isSuccess"
      type="success"
      :message="$t('admin.alertUserInvitationSuccessfullyResent')"
    ></d-alert>
    <d-alert
      v-model="resetSendInvitationAlert.isFailed"
      type="error"
      :message="$t('admin.alertFailedResendUserInvitation')"
    ></d-alert>
    <d-alert
      v-model="resendAllAlert.isSuccess"
      type="success"
      :message="$t('admin.alertAllInvitationResentSuccess')"
    ></d-alert>
    <d-alert
      v-model="resendAllAlert.isFailed"
      type="error"
      :message="$t('admin.alertAllInvitationResentFailed')"
    ></d-alert>
    <!-- delete Invitation Alert -->
    <d-alert
      v-model="deleteInvitationAlert.isSuccess"
      type="success"
      :message="$t('admin.alertUserInvitationSuccessfullyDelete')"
    ></d-alert>
    <d-alert
      v-model="deleteInvitationAlert.isFailed"
      type="error"
      :message="$t('admin.alertFailedDeleteUserInvitation')"
    ></d-alert>
  </div>
</template>

<script>
import Vue from "vue";
import AdminAddUserPopup from "@/components/DAdmin/AdminAddUserPopup.vue";
import AdminFormUserEditor from "@/components/DAdmin/AdminFormUserEditor.vue";
import AdminFormBusinessCreateEdit from "@/components/DAdmin/AdminFormBusinessCreateEdit.vue";
import MixinDB from "@/components/MixinDB.vue";
import MixinUser from "@/components/MixinUser.vue";
import MixinAdmin from "@/components/MixinAdmin.vue";
import MixinAnalytics from "@/components/MixinAnalytics.vue";

import IconBase from "@/components/IconBase.vue";
import DIconSearch from "@/components/icons/DIconSearch.vue";
import DIconTrash from "@/components/icons/DIconTrash.vue";
import DIconLock from "@/components/icons/DIconLock.vue";
import DIconLockOpen from "@/components/icons/DIconLockOpen.vue";
import DTabs from "@/components/ui_components/DTabs.vue";
import DAlert from "@/components/ui_components/DAlert.vue";
import DIconPen from "@/components/icons/DIconPen.vue";
import DIconResend from "@/components/icons/DIconResend.vue";
import DIconClose2 from "@/components/icons/DIconClose2.vue";
import DIconPreview from "@/components/icons/DIconPreview.vue";
import DIconOrgAdmin from "@/components/icons/DIconOrgAdmin.vue";
import DIconSharedUser from "@/components/icons/DIconSharedUser.vue";
import DIconMore from "@/components/icons/DIconMore.vue";
import DConfirmationPopup from "@/components/DPopup/DConfirmationPopup.vue";
import DTooltip from "@/components/ui_components/DTooltip.vue";

import DWorkspaceGroupBreadcrumb from "@/components/ui_components/DWorkspaceGroupBreadcrumb.vue";
import AdminWorkspaceGroups from "@/components/DAdmin/workspaceGroup/AdminWorkspaceGroups.vue";
import AdminSettingsMenu from "@/components/DAdmin/Settings/AdminSettingsMenu.vue";
import DAdminPermissions from "@/components/DAdmin/DAdminPermissions.vue";
import DAdminIpRestriction from "@/components/DAdmin/DAdminIpRestriction.vue";
import AdminCategories from "@/components/DAdmin/AdminCategories.vue";
import AdminLanguages from "@/components/DAdmin/AdminLanguages.vue";
import AdminMasterPassword from "@/components/DAdmin/Settings/AdminMasterPassword.vue";
import AdminCompanyLogo from "@/components/DAdmin/Settings/CompanyLogo/AdminCompanyLogo.vue";
import DAdminSsoMapping from "@/components/DAdmin/DAdminSsoMapping.vue";

import AdminWorkspaceTeams from "@/components/DAdmin/workspaceGroup/AdminWorkspaceTeams.vue";
import DAdminUserCard from "@/components/DAdmin/DAdminUserCard.vue";
import DDataTable from "@/components/ui_components/DDataTable.vue";
import DTableSearch from "@/components/ui_components/DTableSearch.vue";
import DTooltipShowAll from "@/components/ui_components/DTooltipShowAll.vue";
import DAvatar from "@/components/ui_components/DAvatar.vue";
import UpgradeToOrgAdminConfirmation from "@/components/DAdmin/confirmations/UpgradeToOrgAdminConfirmation.vue";
import DowngradeOrgAdminConfirmation from "@/components/DAdmin/confirmations/DowngradeOrgAdminConfirmation.vue";
import UserStatusFilter from "@/components/UserStatusFilter.vue";
import AdminWorkspaceGroupWorkspaces from "@/components/DAdmin/workspaceGroup/AdminWorkspaceGroupWorkspaces.vue";
import { canUpgradeToOrgAdmin, canDowngradeOrgAdmin, showMoreBtnAdminUserList } from "@/js/user-role/user-role.js";
import { appendMissingUserDataFromDB, updateTolatestUserdataFromDb } from "@/js/user/user.js";
import { getBaseDataForUserDataTable, USER_DATA_TABLE_TYPE } from "@/js/user-data-table/user-data-table.js";
import { debounce } from "lodash-es";
import { mapGetters } from "vuex";

export default {
  name: "AdminOrganization",
  watch: {
    groupsData: function (groupsData) {
      this.checkPrivateColumn(groupsData);
    },
    businessDictionary: function () {
      this.reloadGroupBusinessName();
    },
  },
  props: {
    userRole: String,
  },
  data() {
    return {
      settingPage: "",
      //all tabs
      currentTab: 0,
      tabNames: [],
      autotestTabs: [],
      showPart: "allUsers",
      showForm: "",
      formData: {},
      allRoleOptions: ["viewer", "publisher", "admin-group", "admin-organization"],
      //all users
      searchUser: "",
      userTableHeaders: [
        {
          text: this.$t("all.namePerson"),
          align: "left",
          sortable: true,
          value: "displayName",
          autotest: "name-sort",
        },
        {
          text: this.$t("all.email"),
          align: "left",
          sortable: true,
          value: "email",
          autotest: "email-sort",
        },
        {
          text: this.$t("all.employeeId"),
          align: "left",
          sortable: true,
          value: "employeeId",
          autotest: "employee-id-sort",
        },
        {
          text: this.$t("admin.lastActivity"),
          align: "left",
          sortable: true,
          value: "lastActivityTimestamp",
          autotest: "last-activity-sort",
        },
        {
          text: this.$t("admin.status"),
          align: "left",
          sortable: true,
          value: "status",
          autotest: "status-sort",
        },
        {
          text: "",
          align: "right",
          sortable: false,
          value: "",
        },
      ],
      isInvitedFailed: false,
      groupTableHeaders: [
        {
          text: this.$t("all.name"),
          align: "left",
          sortable: true,
          value: "name",
          autotest: "name-sort",
        },
        {
          text: this.$t("analytics.numberOfUsers"),
          align: "left",
          sortable: true,
          value: "numberOfUsers",
          autotest: "number-of-users-sort",
        },
        {
          text: this.$t("all.business"),
          align: "left",
          sortable: true,
          value: "businessName",
          autotest: "business-sort",
        },
        {
          align: "right",
          sortable: false,
          value: "",
        },
      ],
      groupsData: [],
      groupDictionary: {},
      //business
      businessData: [],
      groupId: "",
      businessDictionary: {},
      addBusinessSuccess: false,
      editBusinessSuccess: false,
      isLoading: {
        usersData: false,
        invitationData: false,
        groupsData: false,
        businessData: false,
      },
      resetSendInvitation: false,
      resetSendInvitationAlert: {
        isSuccess: false,
        isFailed: false,
      },
      resendAllAlert: {
        isSuccess: false,
        isFailed: false,
      },
      statusDictionary: {
        active: this.$t("admin.active"),
        inactive: this.$t("admin.inactive"),
        invited: this.$t("admin.invited"),
        "invitation-expired": this.$t("admin.invitationExpired"),
        "could-not-be-invited": this.$t("admin.couldNotInvited"),
      },
      deleteInviteConfirmation: false,
      deleteInvitationAlert: {
        isSuccess: false,
        isFailed: false,
      },
      filterStatus: ["active", "inactive"],
      currentStatus: "active",
      isSearchingUsers: false,
      foundUsers: [],
      noSearchResults: false,
      searchInput: "",
      totalActive: 0,
      totalInactive: 0,
      showPrivateColumn: false,
      viewedUserId: "",
      isGroupsDataReady: false,
    };
  },
  components: {
    DWorkspaceGroupBreadcrumb,
    AdminAddUserPopup,
    AdminFormUserEditor,
    AdminFormBusinessCreateEdit,
    IconBase,
    DIconSearch,
    DIconTrash,
    DIconLock,
    DIconLockOpen,
    DTabs,
    DAlert,
    DIconPen,
    DIconResend,
    DIconClose2,
    DIconPreview,
    DIconOrgAdmin,
    DIconSharedUser,
    DIconMore,
    DConfirmationPopup,
    DTooltip,
    AdminWorkspaceGroups,
    AdminSettingsMenu,
    DAdminPermissions,
    DAdminIpRestriction,
    AdminCategories,
    AdminLanguages,
    AdminMasterPassword,
    AdminCompanyLogo,
    DAdminSsoMapping,
    AdminWorkspaceTeams,
    DAdminUserCard,
    DDataTable,
    DTableSearch,
    DTooltipShowAll,
    DAvatar,
    UpgradeToOrgAdminConfirmation,
    DowngradeOrgAdminConfirmation,
    UserStatusFilter,
    AdminWorkspaceGroupWorkspaces,
  },
  mixins: [MixinDB, MixinUser, MixinAdmin, MixinAnalytics],
  created() {
    const self = this;
    this.auth().onAuthStateChanged((user) => {
      if (user) {
        this.getUserProfileFromVuex();
        // this.getMyAdminData(); //get data for a specific admin level

        self.setCurrentTab();

        this.getBusinessData().then(() => {
          return this.getGroupsData()
            .then(() => this.updateGroupsUsersCount(this.groupsData))
            .then((groups) => {
              self.$set(this.groupsData, groups);
            });
        });

        this.getOrgOverview().then((data) => {
          this.totalActive = data.users.active;
          this.totalInactive = data.users.inactive;
        });
      }
    });
    this.setCustomFeatures();
    if (!this.activeUsersData.length) {
      this.getMoreUsers();
    }
    if (!this.inactiveUsersData.length) {
      this.getMoreInactiveUsers();
    }
  },
  mounted() {
    this.setDefaultSettingPage();
  },
  computed: {
    ...mapGetters(["getter_custom_attributes_with_id_users", "getter_custom_attributes_with_id_inactive_users"]),
    ...mapGetters("permission", [
      "getter_has_employee_id_login",
      "getter_ip_restriction_feature_enabled",
      "getter_is_redirected_by_ip_restriction",
    ]),
    ...mapGetters("workspaceGroups", [
      "getter_admin_workspace_groups",
      "getter_get_admin_workspace_group_by_workspace_id",
    ]),
    activeUsersData() {
      if (this.searchUser) {
        return this.foundUsers;
      }
      return this.getter_custom_attributes_with_id_users;
    },
    inactiveUsersData() {
      if (this.searchUser) {
        return this.foundUsers;
      }
      return this.getter_custom_attributes_with_id_inactive_users;
    },
    hasCustomUserAttributes() {
      try {
        return this.$clientSettings.userMappings.customUserAttributes ? true : false;
      } catch (err) {
        return false;
      }
    },
    customUserAttributes() {
      try {
        return this.$clientSettings.userMappings.customUserAttributes;
      } catch (err) {
        return [];
      }
    },
    userProfile() {
      //get user profile from vuex
      return this.$store.state.userProfile;
    },
    workspaceList() {
      return this.getter_admin_workspace_groups
        .reduce((acc, workspaceGroup) => {
          acc = acc.concat(workspaceGroup.workspaces || []);
          return acc;
        }, [])
        .map((workspace) => {
          const numberOfUsers = this.groupsData.find(({ id }) => id === workspace.id)?.numberOfUsers || 0;
          return {
            ...workspace,
            numberOfUsers,
          };
        });
    },
    businessTableHeaders() {
      return [
        {
          text: this.$t("all.name"),
          align: "left",
          sortable: true,
          value: "name",
          autotest: "business-name-sort",
        },
        {
          text: this.$t("all.workspaceGroup"),
          align: "left",
          sortable: true,
          value: "workspaceGroupName",
          autotest: "workspace-group-name-sort",
        },
        {
          text: this.$t("all.workspace"),
          align: "left",
          sortable: true,
          value: "workspaceName",
          autotest: "workspace-name-sort",
        },
        {
          align: "right",
          sortable: false,
          value: "",
        },
      ];
    },
    businessTableData() {
      const workspaceWithBusinsess = this.groupsData.filter((workspace) => workspace.business);
      const businessesNoWorkspaces = this.businessData.reduce((acc, business) => {
        const workspace = workspaceWithBusinsess.find((workspace) => workspace.business === business.id);
        if (!workspace) {
          acc.push({
            ...business,
            workspaceName: "",
            workspaceGroupName: "",
          });
        }
        return acc;
      }, []);
      const businessesWithWorkspaces = this.groupsData.reduce((acc, workspace) => {
        const business = this.businessData.find((business) => business.id === workspace.business);
        const workspaceGroupName = this.getter_get_admin_workspace_group_by_workspace_id(workspace.id)?.name;
        if (business) {
          acc.push({
            ...business,
            workspaceName: workspace.name,
            workspaceGroupName,
          });
        }
        return acc;
      }, []);
      const data = businessesNoWorkspaces.concat(businessesWithWorkspaces);
      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  methods: {
    setDefaultSettingPage() {
      if (this.getter_is_redirected_by_ip_restriction) {
        return (this.settingPage = "ip-restriction");
      }
      this.settingPage = this.$access.categoriesCreation ? "categories" : "permissions";
    },
    setSettingPage(settingPage) {
      this.$router.push("/admin/settings");
      this.showPart = "settings";
      this.settingPage = settingPage;
    },
    checkPrivateColumn(groupsData) {
      const addedPrivate = this.groupTableHeaders.find((group) => group.value === "private");
      const hasPrivate = groupsData.find((group) => group.private === true);
      if (hasPrivate && !addedPrivate) {
        this.showPrivateColumn = true;
        const header = {
          text: this.$t("all.private"),
          align: "left",
          sortable: true,
          value: "private",
          autotest: "private-sort",
        };
        this.groupTableHeaders.splice(3, 0, header);
      } else if (!hasPrivate && addedPrivate) {
        this.showPrivateColumn = false;
        this.groupTableHeaders.splice(4, 1);
      } else if (hasPrivate) {
        this.showPrivateColumn = true;
      }
    },
    updateFoundUser(update, type) {
      if (this.searchUser) {
        if (type === "update") {
          let updateUser = this.foundUsers.find((foundUser) => foundUser.id == update.user.id);
          if (updateUser) {
            for (const key in update.update) {
              updateUser[key] = update.update[key];
            }
          }
          this.foundUsers.splice();
        }
      }
    },
    updateTolatestUserdataFromDb({ targetUserData }) {
      updateTolatestUserdataFromDb({
        mixinDbRef: this,
        userDataRef: targetUserData,
      });
    },
    canUpgradeToOrgAdmin(userData) {
      return canUpgradeToOrgAdmin({
        userData,
        vueInstanceRef: this,
      });
    },
    canDowngradeOrgAdmin(userData) {
      return canDowngradeOrgAdmin({
        userData,
        vueInstanceRef: this,
      });
    },
    showMoreBtn(userData) {
      return showMoreBtnAdminUserList({
        userData,
        vueInstanceRef: this,
      });
    },
    clickViewUserCard(id) {
      this.viewedUserId = this.viewedUserId === id ? "" : id;
    },
    debounceHandleFindUser: debounce(async function (searchInput) {
      await this.handleFindUser(searchInput);
    }, 500),
    async handleFindUser(searchInput) {
      if (searchInput.length === 0) {
        return;
      }
      this.noSearchResults = false;
      this.isSearchingUsers = true;
      try {
        const response = await this.findUser(searchInput, null, false);
        const query = JSON.parse(response.config.data).text;
        if (searchInput == query) {
          const results = response.data.results;
          const fResults = this.formatUserSearchResults(results);
          if (!fResults.length) {
            this.noSearchResults = true;
          } else {
            this.noSearchResults = false;
          }

          const foundUsers = fResults.splice(0, 100);
          if (foundUsers.length) {
            await appendMissingUserDataFromDB({
              mixinDbRef: this,
              userListRef: foundUsers,
            });

            foundUsers.forEach((user) => {
              Object.assign(
                user,
                getBaseDataForUserDataTable({
                  user,
                  dataTableType: user.invitation ? USER_DATA_TABLE_TYPE.INVITED : this.currentStatus,
                })
              );
            });
          }
          this.foundUsers = foundUsers;
          this.isSearchingUsers = false;
        }
      } catch (e) {
        console.error(e);
      }
    },
    getMoreInactiveUsers() {
      this.isLoading.inactiveUsersData = true;
      this.$store.dispatch("getInactiveUsers", {
        organization: this.$organization,
      });
      this.$store.subscribe(
        function (mutation, state) {
          if (mutation.type == "getInactiveUsersEnd") {
            this.isLoading.inactiveUsersData = false;
          }
        }.bind(this)
      );
    },
    getMoreUsers() {
      this.isLoading.usersData = true;
      this.$store.dispatch("getUsers", { organization: this.$organization });
      this.$store.subscribe(
        function (mutation, state) {
          if (mutation.type == "getUsersEnd") {
            this.isLoading.usersData = false;
          }
        }.bind(this)
      );
    },
    setCustomFeatures() {
      if (this.$access.teams) {
        this.tabNames = [
          this.$t("all.workspaceGroup"),
          this.$t("all.workspaces"),
          this.$t("all.teams"),
          this.$t("all.users"),
          this.$t("all.business"),
        ];
        this.autotestTabs = ["workspace-groups-tab", "workspaces-tab", "teams-tab", "all-users-tab", "business-tab"];
      } else {
        this.tabNames = [this.$t("all.allUsers"), this.$t("all.business")];
        this.autotestTabs = ["all-users-tab", "business-tab"];
      }
      this.tabNames.push(AdminSettingsMenu);
      this.autotestTabs.push("settings-tab");
    },
    deleteInvite() {
      const invite = this.formData;
      this.deleteUserInvite(invite)
        .then(() => {
          this.$store.dispatch("updateInvitation", {
            invite: invite,
            action: "remove",
          });
          this.deleteInvitationAlert.isSuccess = true;
        })
        .catch((err) => {
          this.deleteInvitationAlert.isFailed = true;
        });
    },
    setDeleteInvitation(user) {
      this.formData = user;
      this.deleteInviteConfirmation = true;
    },
    resendInvitation() {
      const invite = this.formData;
      invite.status = "invited";
      this.updateDocument("invitations", this.formData.id, {
        status: "pending",
      })
        .then(() => {
          this.$store.dispatch("updateInvitation", {
            invite: invite,
            action: "update",
          });
          this.resetSendInvitationAlert.isSuccess = true;
        })
        .catch((err) => {
          this.resetSendInvitationAlert.isFailed = true;
        });
    },
    setResendInvitation(user) {
      this.formData = user;
      this.resetSendInvitation = true;
    },
    getTabAndRouteInfo(checkByIndex, params = { tabIndex, tabValue }) {
      const tabList = this.$access.teams
        ? ["main", "workspaces", "teams", "allUsers", "business", "settings"]
        : ["main", "business", "settings"];
      let tabIndex;
      if (checkByIndex) {
        const value = tabList[params.tabIndex];
        tabIndex = !value ? 0 : params.tabIndex;
      } else {
        const index = tabList.indexOf(params.tabValue);
        tabIndex = index === -1 ? 0 : index;
      }
      const tabValue = tabList[tabIndex];
      let showPart;
      if (tabValue === "main") showPart = this.$access.teams ? "workspaceGroups" : "allUsers";
      else showPart = tabValue;
      return {
        tabIndex,
        tabValue,
        showPart,
        routeSubPathName: tabValue,
      };
    },
    clickTab(tabIndex) {
      this.searchUser = "";
      const info = this.getTabAndRouteInfo(true, { tabIndex });
      this.showPart = info.showPart;
      this.$router.push(`/admin/${info.routeSubPathName}`);
    },
    setCurrentTab() {
      const tabValue = this.$route.params.tab;
      const info = this.getTabAndRouteInfo(false, { tabValue });
      this.currentTab = info.tabIndex;
      this.clickTab(this.currentTab);
    },
    getBusinessName(id) {
      return this.businessDictionary[id] || "-";
    },
    reloadGroupBusinessName() {
      this.isLoading.groupsData = true;
      this.groupsData.forEach((group) => {
        group.businessName = this.getBusinessName(group.business);
      });
      this.isLoading.groupsData = false;
    },
    getGroupsData() {
      const self = this;
      this.isGroupsDataReady = false;
      return new Promise(function (resolve, reject) {
        self.isLoading.groupsData = true;
        self.fetchGroups().then((data) => {
          // add businessName to groupsData
          data.forEach((group) => {
            group.businessName = self.getBusinessName(group.business);
          });
          data.forEach((group) => {
            self.groupDictionary[group.id] = group.name;
          });
          self.updateGroupsUsersCount(data).then((groups_data_width_num_of_user) => {
            self.groupsData = groups_data_width_num_of_user;
            self.isLoading.groupsData = false;
            self.isGroupsDataReady = true;
            resolve();
          });
        });
      });
    },
    fetchGroups() {
      const self = this;
      const userId = self.$userProfile.id;
      return new Promise(function (resolve, reject) {
        self.getDocumentByFieldValue("groups", "organization", self.$organization).then((data) => {
          resolve(data);
        });
      });
    },
    reloadBusinessData() {
      this.businessData = [];
      this.isLoading.businessData = true;
      this.getBusinessData();
    },
    getBusinessData() {
      this.isLoading.businessData = true;
      return this.fetchBusinesses().then((data) => {
        this.businessData = data;
        this.isLoading.businessData = false;
      });
    },
    fetchBusinesses() {
      const self = this;
      return new Promise(function (resolve, reject) {
        self
          .getDocumentByQuery("businesses", [
            {
              field: "organization",
              comparison: "==",
              value: self.$organization,
            },
          ])
          .then((data) => {
            data.forEach((business) => {
              self.businessDictionary[business.id] = business.name;
            });
            self.businessDictionary = Object.assign({}, self.businessDictionary, {});
            resolve(data);
          });
      });
    },
    setUserEditor(user) {
      this.formData = {};
      this.formData = user;
      this.showForm = "user-editor";
    },
    setEditorFormData(data) {
      this.formData = {};
      this.formData = data;
    },
    getUserProfileFromVuex() {
      Vue.prototype.$user = this.auth().currentUser;
      this.$store.dispatch("getUserProfile", this.$user);
    },
    showUpgradeOrgPopup(user) {
      this.formData = user;
      this.showForm = "upgrade-to-org";
    },
    showDowngradeOrgPopup(user) {
      this.formData = user;
      this.showForm = "downgrade-org";
    },
    changeCurrentStatus(status) {
      this.currentStatus = status;
    },
  },
};
</script>

<style scoped lang="scss">
.adminOrganization {
  height: 100%;
  width: 100%;
  background-color: #fbfbfb;

  &__title {
    height: 148px;
    padding: 44px 28px 0 28px;
    text-align: left;
  }

  &__tabs {
    margin-top: 30px;

    // TODO: Refactor with `AdminSettingsMenu` of tabs.
    &-focus-settings ::v-deep .blue-bar {
      height: 0 !important;
    }

    &__settingsMenu {
      position: relative;
      height: 100%;
      display: flex;
    }
  }
}
.admin-organization {
  width: 100%;
  padding: 0;
  background-color: #fbfbfb;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tab-div {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  margin-bottom: 8px;
}
.tab-title-row {
  position: relative;
  height: 90px;
  width: 100%;
  display: flex;
  padding: 30px 28px 0 28px;
}
.tab-title {
  height: 44px;
  width: 145px;
  color: #3f3f3f;
  font-family: Roboto;
  font-size: 32px;
  letter-spacing: 0;
  line-height: 44px;
}

.create-btn {
  min-width: 140px;
  text-transform: none !important;
}

.new-btns-td {
  text-align: left;
  min-width: 172px;
  padding: 0 24px;
  height: 100%;
  position: relative;
}
.new-btns-td-div {
  display: flex;
}

.view-button-new {
  position: relative;
  height: 22px;
  width: 22px;
  color: #78767a;
  margin-right: 20px;
}

.hide-view-btn {
  opacity: 0.3;
  opacity: 0;
  pointer-events: none;
}
.tooltip-btn-new {
  position: relative;
  height: 22px;
  width: 22px;
  margin-right: 20px;
}
.cancel-invite-btn-new {
  position: relative;
  height: 18px;
  width: 18px;
}
.cancel-invite-btn {
  position: relative;
  display: inline-flex;
  height: 22px;
  width: 22px;
  text-align: center;
  margin-top: 14px;
  padding: 2px;
}
.tooltip-btn-btn {
  color: #78767a;
  opacity: 0.8;
}
.table-editor-btn {
  padding-top: 8px;
  color: #78767a;
  margin-right: 8px;
}
.btn-hover:hover {
  color: #2d2e32;
  cursor: pointer;
}

.admin-setting-div {
  position: relative;
  height: 100%;
  width: 100%;
}
/* //// d-data-table ///// */
.center-horizontally {
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
}
.tab-title-new {
  height: 44px;
  width: 145px;
  color: #3f3f3f;
  font-family: Roboto;
  font-size: 32px;
  letter-spacing: 0;
  line-height: 44px;
}
.table-div {
  width: 100%;
  height: 100%;
  padding: 0 28px;
}
.table-search-row {
  position: relative;
  width: 100%;
  padding: 0 28px;
}
.d-data-table {
  position: relative;
  height: 100%;
  width: 100%;
}
.filter-div {
  position: relative;
}
.avatar-div {
  position: relative;
  display: inline-block;
  height: 32px;
  width: 32px;
}
.org-admin-icon {
  position: absolute;
  right: -10px;
  bottom: -4px;
  height: 20px;
  width: 20px;
  background-color: #ffbf18;
  border-radius: 10px;
  padding: 3px;
}
.shared-icon {
  background-color: #a9a9a9;
}
.table-wrap {
  height: 100%;
  padding: 0 28px;
}
.table-wrap:hover::-webkit-scrollbar-thumb {
  background-color: #b6b6b6;
}
.user-card {
  z-index: 2;
}
@media screen and (max-width: 959px) {
  .tab-title-row {
    padding: 30px 20px 0 20px;
  }
  .table-div {
    padding: 0 20px;
  }
  .table-search-row {
    padding: 0 20px;
  }
  .table-wrap {
    padding: 0 20px;
  }
}
@media screen and (min-width: 960px) and (max-width: 1903px) {
  .tab-title-row {
    padding: 30px 28px 0 28px;
  }
}
</style>
